import { Linkable } from '@/components/blocks/Linkable';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { UserLogon } from '@/data/Content/Login';
import { useLocalization } from '@/data/Localization';
import { REG_EX } from '@/utils/address';
import { useForm } from '@/utils/useForm';
import { Stack } from '@mui/material';
import { FC, useState } from 'react';
import {
	SingleLoginFormEmailLabelSX,
	SingleLoginFormForgotPasswordBottomSX,
	SingleLoginFormForgotPasswordSX,
} from '@/components/blocks/SingleLoginForm/style';
import { DpBaseButton } from '@/components/blocks/DpBaseButton';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';

const initLoginValues: UserLogon = {
	logonId: '',
	logonPassword: '',
	rememberMe: false,
};

const errorsFormFields = {
	email: {
		empty: {
			errorMsg: 'Campo obligatorio',
		},
		formatIncorrect: {
			errorMsg: 'Por favor ingresa un correo electrónico válido',
		},
	},
	logonPassword: {
		empty: {
			errorMsg: 'Campo obligatorio',
		},
		rules: {
			errorMsg: 'Mínimo 1 letra, 1 número y 6 caractéres',
		},
	},
};

type Props = {
	signInButtonLabel?: string;
	disabled?: boolean;
	forgotPasswordPosition?: 'form' | 'bottom' | 'line';
	onSubmit: (value: UserLogon) => void;
};

export const SingleLoginForm: FC<Props> = ({
	signInButtonLabel,
	forgotPasswordPosition,
	disabled,
	onSubmit,
}) => {
	const RouteLocal = useLocalization('Routes');
	const signInNLS = useLocalization('SignIn');

	const {
		handleInputChange,
		values: loginValues,
		formRef,
		// error,
		handleSubmit,
		submitting,
	} = useForm(initLoginValues);
	const [errorEmail, setErrorEmail] = useState<any>({ error: false, errorMsg: '' });
	const [errorLogonPassword, setErrorLogonPassword] = useState<any>({ error: false, errorMsg: '' });

	const handleOnBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
		const name = _event.target.name;
		const value = _event.target.value;

		if (name === 'email') {
			if (value.trim() === '') {
				setErrorEmail({
					error: true,
					errorMsg: errorsFormFields[name].empty.errorMsg,
				});
			} else if (!REG_EX.EMAIL.test(value)) {
				setErrorEmail({
					error: true,
					errorMsg: errorsFormFields[name].formatIncorrect.errorMsg,
				});
			} else {
				setErrorEmail({
					error: false,
					errorMsg: '',
				});
			}
		}

		if (name === 'logonPassword') {
			if (value.trim() === '') {
				setErrorLogonPassword({
					error: true,
					errorMsg: errorsFormFields[name].empty.errorMsg,
				});
			} else {
				setErrorLogonPassword({
					error: false,
					errorMsg: '',
				});
			}
		}
	};

	return (
		<Stack width="100%" component="form" onSubmit={handleSubmit(onSubmit)} noValidate ref={formRef}>
			<DpOutlinedInput
				sx={{
					...addressFormFieldsSX,
					'.MuiOutlinedInput-input': {
						padding: '14px 14px',
						fontSize: 'var(--font-size-body)',
						height: '20px',
					},
					mb: '22px',
				}}
				variant="outlined"
				margin="normal"
				fullWidth
				required
				name="email"
				autoComplete="email"
				id="email-sign-in-submit"
				label="Correo electrónico"
				onBlur={handleOnBlur}
				error={errorEmail.error}
				helperText={errorEmail.errorMsg}
				value={loginValues.email}
				onChange={handleInputChange}
				InputLabelProps={{
					sx: SingleLoginFormEmailLabelSX,
				}}
				inputProps={{
					maxLength: 100,
					type: 'email',
					pattern: REG_EX.EMAIL.source,
				}}
				// error={error.email}
				// helperText={
				// 	error.email
				// 		? !loginValues.email
				// 			? 'Campo obligatorio'
				// 			: 'Formato no válido'
				// 		: EMPTY_STRING
				// }
				FormHelperTextProps={{
					sx: { ml: '3px' },
				}}
			/>

			<PasswordInput
				sx={addressFormFieldsSX}
				required
				name="logonPassword"
				label="Contraseña"
				value={loginValues.logonPassword}
				// error={error.logonPassword}
				inputProps={{
					maxLength: 40,
					pattern: REG_EX.PASSWORD.source,
				}}
				onBlur={handleOnBlur}
				error={errorLogonPassword.error}
				helperText={errorLogonPassword.errorMsg}
				onChange={handleInputChange}
			/>

			{forgotPasswordPosition === 'form' ? (
				<Linkable
					href={`/${RouteLocal.ForgotPassword.route.t()}`}
					type="inline"
					sx={SingleLoginFormForgotPasswordSX}
					data-testid={RouteLocal.ForgotPassword.route.t()}
					id={RouteLocal.ForgotPassword.route.t()}
				>
					{signInNLS.ForgotPassword.t()}
				</Linkable>
			) : null}

			<DpBaseButton
				sx={{ mt: '20px' }}
				id="button-sign-in-submit"
				type="submit"
				disabled={submitting || disabled}
			>
				{signInButtonLabel ?? 'Iniciar sesión'}
			</DpBaseButton>

			{forgotPasswordPosition === 'bottom' || forgotPasswordPosition === 'line' ? (
				<Stack direction="row" justifyContent="center">
					<Linkable
						href={`/${RouteLocal.ForgotPassword.route.t()}`}
						type="inline"
						sx={SingleLoginFormForgotPasswordBottomSX}
						data-testid={RouteLocal.ForgotPassword.route.t()}
						id={RouteLocal.ForgotPassword.route.t()}
					>
						{signInNLS.ForgotPassword.t()}
					</Linkable>
				</Stack>
			) : null}
		</Stack>
	);
};
